import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiArrowLeft, FiClock, FiMessageCircle, FiPhoneCall } from 'react-icons/fi';
import API from '../api/axios';
import './Login.css';

const formatTimer = (totalSeconds) => {
  if (totalSeconds <= 0) return '00:00';
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const ForgotPassword = () => {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  const isValid = /^[6-9]\d{9}$/.test(phone);

  // Tick down timer every second
  useEffect(() => {
    if (cooldownSeconds > 0) {
      timerRef.current = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [cooldownSeconds > 0]);

  // Check rate-limit cooldown status whenever a valid 10-digit phone is entered
  useEffect(() => {
    let active = true;
    if (isValid) {
      API.get(`/auth/otp-status?phone=${phone}`)
        .then(({ data }) => {
          if (active && data.cooldownActive && data.remainingSeconds > 0) {
            setCooldownSeconds(data.remainingSeconds);
          }
        })
        .catch(() => {});
    } else {
      setCooldownSeconds(0);
    }
    return () => {
      active = false;
    };
  }, [phone, isValid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      toast.error('Please enter a valid 10-digit Indian phone number');
      return;
    }
    if (cooldownSeconds > 0) {
      toast.error(`Please wait ${formatTimer(cooldownSeconds)} before requesting another OTP.`);
      return;
    }
    setLoading(true);

    try {
      const { data } = await API.post('/auth/forgot-password', { phone });
      toast.success(data.message || 'OTP sent to your phone!');
      if (data.devOtp) {
        toast.success(`Dev OTP: ${data.devOtp}`);
      }

      const nextCooldown = data.cooldownSeconds || 600;
      try {
        sessionStorage.setItem('sc_reset_phone', phone);
      } catch (e) {}

      navigate('/reset-password', {
        state: {
          phone,
          initialCooldownSeconds: nextCooldown,
          cooldownUntil: data.cooldownUntil
        }
      });
    } catch (error) {
      const res = error.response?.data;
      if (error.response?.status === 429) {
        const secs = res?.retryAfterSeconds || (res?.retryAfterMinutes ? res.retryAfterMinutes * 60 : 600);
        setCooldownSeconds(secs);
        toast.error(res?.error || `Rate limit active. Please wait ${formatTimer(secs)}.`);
      } else {
        toast.error(res?.error || 'Failed to send OTP');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-header">
        <div className="login-logo">🖨️</div>
        <h1 className="login-title">Shubham Computers</h1>
        <p className="login-tagline">Print & Stationery Mart</p>
        <p className="login-sub">Jhajjar • Delivery under 30 mins ⚡</p>
      </div>

      <div className="login-card">
        <form onSubmit={handleSubmit}>
          <h2 className="login-form-title">Forgot Password</h2>
          <p className="login-otp-info">Enter your registered phone number to receive an OTP.</p>

          <div className="otp-call-notice">
            <FiPhoneCall size={16} />
            <span>
              <strong>Heads up:</strong> You may receive your OTP via SMS or an <strong>automated phone call</strong>. Please answer your phone if it rings!
            </span>
          </div>

          <div className="phone-input-group">
            <span className="phone-prefix">+91</span>
            <input
              type="tel"
              className="phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="Enter phone number"
              required
              maxLength="10"
              autoFocus
            />
          </div>

          {cooldownSeconds > 0 && (
            <div className="rate-limit-banner">
              <FiClock size={16} />
              <span>Resend available in {formatTimer(cooldownSeconds)}</span>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-full login-submit-btn"
            disabled={loading || !isValid || cooldownSeconds > 0}
          >
            {loading
              ? 'Sending OTP...'
              : cooldownSeconds > 0
              ? `Resend in ${formatTimer(cooldownSeconds)}`
              : 'Send OTP'}
          </button>

          <div>
            <Link to="/login" className="back-to-login">
              <FiArrowLeft size={16} />
              <span>Back to Login</span>
            </Link>
          </div>

          <div className="whatsapp-help-box">
            <p className="whatsapp-help-text">Did not receive SMS OTP? Contact Shubham Cyber Cafe directly:</p>
            <a
              href={`https://wa.me/919350336367?text=${encodeURIComponent(`Hi Shubham, I need help resetting my password on ShubInk for phone: ${phone || ''}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-help-btn"
            >
              <FiMessageCircle size={16} />
              <span>Get Help on WhatsApp</span>
            </a>
          </div>
        </form>
      </div>

      <p className="login-footer">
        By continuing, you agree to our <Link to="/terms" className="privacy-link">Terms</Link> & <Link to="/privacy-policy" className="privacy-link">Privacy Policy</Link>
      </p>
    </div>
  );
};

export default ForgotPassword;
